"use client";

import { typeByExtension } from "@std/media-types";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import { useVideoAudioRegistration } from "@/components/ui/media/VideoAudioContext";
import { usePageVisible } from "@/lib/hooks/usePageVisible";

export interface VideoPlayerProps extends ComponentProps<"video"> {
	src: string;
	/**
	 * Opt in to audio. Registers this video with the nearest
	 * `VideoAudioProvider` so a `VideoAudioToggle` can unmute it, and adds the
	 * guards that keep sound from playing off-screen or in a hidden tab.
	 *
	 * Left off (the default) the video behaves exactly as it always has:
	 * permanently muted, with no observer and no listeners attached.
	 */
	sound?: boolean;
}

const getStreamVideoUrl = (url: string) => {
	return `https://streams.frend.dev/video/v1?url=${encodeURIComponent(url)}`;
};

const getMimeType = (url: string) => {
	const mimeType = typeByExtension(url.split(".").pop() || "");
	return mimeType;
};

export const VideoPlayer = ({ src, sound = false, ...rest }: VideoPlayerProps) => {
	const ref = useRef<HTMLVideoElement>(null);
	const audio = useVideoAudioRegistration();
	const pageVisible = usePageVisible();
	const [onScreen, setOnScreen] = useState(true);

	// `register` is referentially stable; depending on the whole context value
	// would re-run this on every mute toggle.
	const register = audio?.register;

	useEffect(() => {
		const el = ref.current;
		if (!sound || !register || !el) return;
		return register(el);
	}, [sound, register]);

	// Sound should never come from something the visitor can't see.
	useEffect(() => {
		const el = ref.current;
		if (!sound || !el) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry) setOnScreen(entry.isIntersecting);
			},
			{ threshold: 0.25 },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [sound]);

	// One expression owns the real mute state so its inputs can't race each
	// other. Note this also means scrolling back into view, or returning to the
	// tab, leaves the video muted until the visitor asks again — deliberate.
	const shouldBeMuted = !sound || !audio || audio.muted || !onScreen || !pageVisible;

	// Applied imperatively, and `muted` stays hardcoded below: React sets
	// `muted` as a DOM property and never serialises it into the SSR HTML, so a
	// JSX-driven `muted={false}` can reach the browser as an unmuted video and
	// have its autoplay blocked outright. React diffs the attribute against its
	// own previous value (`true` -> `true`), so it won't undo this write.
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		el.muted = shouldBeMuted;
	}, [shouldBeMuted]);

	return (
		<div className="relative h-full w-full">
			<video
				ref={ref}
				autoPlay
				loop
				muted
				playsInline
				{...rest}
				className="absolute inset-0 h-full w-full object-cover"
			>
				<source src={getStreamVideoUrl(src)} type={getMimeType(src)} />
				<source src={getStreamVideoUrl(src)} />
			</video>
		</div>
	);
};
